<template>
  <v-form ref="ccForm">
    <div v-show="formReady && displayCompanySwitch">
      <div class="company-switch d-flex">
        <div
          @click="setIsCompany(true)"
          class="company-switch__item"
          :class="{ 'company-switch__item--active': isCompany }">
          <v-icon size="16">mdi-domain</v-icon>
          {{ $t("settings.billing.stripe_form.company") }}
        </div>
        <div
          @click="setIsCompany(false)"
          class="company-switch__item"
          :class="{ 'company-switch__item--active': !isCompany }">
          <v-icon size="16">mdi-account</v-icon>
          {{ $t("settings.billing.stripe_form.individual") }}
        </div>
      </div>

      <p class="stripe-form-disclaimer">
        {{ $t("billing.billing_information") }}
      </p>
    </div>

    <div id="stripe-payment-element"></div>

    <template v-if="type === 'payment'">
      <div id="stripe-address-element" class="spacing-top--16"></div>
      <va-text-input
        v-if="formReady && isCompany"
        class="spacing-top--24"
        v-model="taxId"
        :label="$t('settings.billing.stripe_form.card_taxid_label')"
        :placeholder="$t('settings.billing.stripe_form.card_taxid_placeholder')"
        :blur="onBlur"></va-text-input>
    </template>
  </v-form>
</template>

<script>
  export default {
    name: "StripeForm",
    props: {
      purchaseItem: {
        type: Object,
      },
      defaultValues: {
        type: Object,
        default: () => ({}),
      },
      onBlur: {
        type: Function,
        default: () => {},
      },
      type: {
        type: String,
        default: "payment",
        validator: function (value) {
          return ["payment", "paymentMethod"].includes(value);
        },
      },
      clientSecret: {
        type: String,
        default: "",
      },
      displayCompanySwitch: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        stripe: null,
        elements: null,
        addressElement: null,
        paymentElement: null,
        taxId: "",
        cardElement: null,
        isCompany: true,
        formReady: false,
      };
    },
    async mounted() {
      this.stripe = await this.$store.dispatch("billing/getStripe");
      this.createStripeElements();
      this.createPaymentElement();
      if (this.type === "payment") {
        this.createAddressElement(this.isCompany);
      }
    },

    methods: {
      setIsCompany(isCompany) {
        this.isCompany = isCompany;
        this.addressElement.destroy();
        this.createAddressElement(this.isCompany);
      },
      createAddressElement(isCompany) {
        this.addressElement = this.elements.create("address", {
          mode: "billing",
          defaultValues: {
            /**
             * If no default value for company is provided, the "Organization name" input is empty,
             * there's no way to even add a placeholder (stripe the best:)
             */
            name: isCompany ? this.getCompanyName() : `${this.defaultValues.firstName} ${this.defaultValues.lastName}`,
          },
          fields: {
            phone: "never",
          },
          display: {
            name: isCompany ? "organization" : "full",
          },
        });

        this.addressElement.on("ready", () => {
          this.formReady = true;
          this.$emit("ready");
        });

        this.addressElement.on("change", event => {
          if (event.complete) {
            this.onBlur({ valid: true });
          }
        });

        this.addressElement.mount("#stripe-address-element");
      },
      getCompanyName() {
        const firstName = this.defaultValues.firstName;

        return firstName.slice(-1) === "s" ? `${firstName}' Organization` : `${firstName}'s Organization`;
      },
      createPaymentElement() {
        this.paymentElement = this.elements.create("payment", {
          terms: {
            card: "never",
          },
        });

        this.paymentElement.on("change", event => {
          if (event.complete) {
            this.onBlur({ valid: true });
          }
        });

        this.paymentElement.on("ready", () => {
          this.$emit("ready");
        });

        this.paymentElement.mount("#stripe-payment-element");
      },

      createStripeElements() {
        const fonts = [
          {
            cssSrc: "https://fonts.googleapis.com/css?family=Inter:100,300,400,500,700,900",
          },
        ];

        // https://docs.stripe.com/elements/appearance-api
        const appearance = {
          variables: {
            fontFamily: `"Inter", sans-serif`,
            borderRadius: "4px",
            colorPrimary: "#6A49EF",
            gridRowSpacing: "24px",
          },
          rules: {
            ".Label": {
              color: "#6C6C7D",
              fontSize: "12px",
              lineHeight: "16px",
              fontWeight: 700,
            },
            ".Input": {
              color: "#45404E",
              fontSize: "14px",
              lineHeight: "24px",
              fontWeight: 400,
              borderColor: "#DAD9DD",
              boxShadow: "none",
            },
            ".Input:focus": {
              borderColor: "#6a49ef",
              boxShadow: "none",
            },
            ".Input--invalid": {
              borderColor: "#FF4C51",
              boxShadow: "none",
              color: "#45404E",
            },
            ".Error": {
              color: "#FF4C51",
              marginTop: "8px",
              fontSize: "12px",
              lineHeight: "16px",
              fontWeight: 400,
            },
            ".TermsText": {
              color: "#45404E",
            },
          },
        };

        if (this.type === "payment") {
          this.elements = this.stripe.elements({
            mode: "subscription",
            /**
             * Amount is not always shown. Depends on the payment method.
             * Amount should reflect what a customer is charged immediately and since
             * we initially start a free trial, sending a 0 here
             * https://docs.stripe.com/js/elements_object/create_without_intent
             */
            amount: 0,
            currency: "usd",
            locale: "en",
            // https://docs.stripe.com/js/elements_object/create#stripe_elements-options-fonts
            fonts,
            appearance,
            loader: "always",
          });
        }

        if (this.type === "paymentMethod") {
          this.elements = this.stripe.elements({
            clientSecret: this.clientSecret,
            locale: "en",
            // https://docs.stripe.com/js/elements_object/create#stripe_elements-options-fonts
            fonts,
            appearance,
          });
        }
      },

      async submit() {
        const { error } = await this.elements.submit();
        const formValid = this.$refs.ccForm.validate();

        if (error || !formValid) {
          return;
        }

        this.$emit("submit", {
          taxId: this.taxId,
          addressElement: this.addressElement,
          paymentElement: this.paymentElement,
          stripe: this.stripe,
          elements: this.elements,
          company: this.isCompany,
        });
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import "../../@core/preset/preset/typography/index";
  @import "../../@core/preset/preset/functions.scss";

  .stripe-form-disclaimer {
    @include text-body-2();
    color: get-color("body");
    margin: 24px 0;
  }

  .company-switch {
    gap: 4px;
    &__item {
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      flex: 1;
      color: get-color("subtitle");
      @include text-body-2();
      padding: 6px 24px;
      border: 1px solid get-color("border");
      border-radius: 4px;
      .v-icon {
        margin-right: 8px;
        color: get-color("subtitle");
      }
      &--active {
        color: get-color("headline");
        border: 1px solid get-color("primary");
        .v-icon {
          color: get-color("headline");
        }
      }
    }
  }
</style>
